import { useState, useMemo, useCallback } from 'react';
import { CheckCircle, XCircle, DollarSign, ChevronDown, ChevronRight, X, Building2, Search, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/core/components/ui/card';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/core/components/ui/table';
import type { PublishTariffsResult } from '../../types/connection-status-types';
import { cn } from '@/core/lib/utils';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';

/**
 * Highlights matching text with yellow background
 */
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase().trim();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) return <>{text}</>;

  const before = text.slice(0, index);
  const match = text.slice(index, index + lowerQuery.length);
  const after = text.slice(index + lowerQuery.length);

  return (
    <>
      {before}
      <span className="bg-yellow-200 px-0.5 rounded">{match}</span>
      {after}
    </>
  );
}

interface PublishTariffsResultPanelProps {
  result: PublishTariffsResult;
  onClose?: () => void;
}

export function PublishTariffsResultPanel({ result, onClose }: PublishTariffsResultPanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showPublishedTariffs, setShowPublishedTariffs] = useState(false); // Collapsed by default
  const [actorSearchQuery, setActorSearchQuery] = useState('');
  const messageIds = result.response.messageIds ?? [];
  const publishedTariffs = result.response.publishedTariffs ?? [];
  const hasPublishedTariffs = publishedTariffs.length > 0;

  // Filter and sort published tariffs by search query (actor name, ID, tariff type, or relation ID)
  const filteredPublishedTariffs = useMemo(() => {
    let filtered = publishedTariffs;

    if (actorSearchQuery.trim()) {
      const query = actorSearchQuery.toLowerCase().trim();
      filtered = publishedTariffs.filter(
        (tariff) =>
          (tariff.actorName?.toLowerCase().includes(query)) ||
          tariff.actorId?.toString().includes(query) ||
          tariff.tariffType?.toLowerCase().includes(query) ||
          tariff.relationId?.toString().includes(query)
      );
    }

    // Sort by actor name (alphabetically), with null names at the end
    return [...filtered].sort((a, b) => {
      const nameA = a.actorName ?? '';
      const nameB = b.actorName ?? '';
      if (!nameA && nameB) return 1;
      if (nameA && !nameB) return -1;
      return nameA.localeCompare(nameB);
    });
  }, [publishedTariffs, actorSearchQuery]);

  return (
    <Card className={result.isSuccess ? 'border-green-200' : 'border-red-200'}>
      <CardHeader className={`${result.isSuccess ? 'bg-green-50' : 'bg-red-50'} cursor-pointer`}>
        <div className="flex items-center justify-between">
          <div
            className="flex items-center gap-3 flex-1"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
            {result.isSuccess ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <XCircle className="h-6 w-6 text-red-600" />
            )}
            <div>
              <CardTitle
                className={`text-lg ${result.isSuccess ? 'text-green-800' : 'text-red-800'}`}
              >
                <DollarSign className="h-5 w-5 inline mr-2" />
                Publish Tariffs Result
                <span className="ml-2 text-sm font-normal">
                  ({hasPublishedTariffs ? publishedTariffs.length : messageIds.length} tariffs)
                </span>
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                {new Date(result.timestamp).toLocaleString()}
              </p>
            </div>
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>

      {!isCollapsed && (
        <CardContent className="pt-4 space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div className={result.isSuccess ? 'bg-green-50' : 'bg-red-50'} >
            <div className="rounded-lg p-3 text-center">
              <p className={`text-2xl font-bold ${result.isSuccess ? 'text-green-700' : 'text-red-700'}`}>
                {result.isSuccess ? 'Success' : 'Failed'}
              </p>
              <p className={`text-sm ${result.isSuccess ? 'text-green-600' : 'text-red-600'}`}>
                Status
              </p>
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">
              {hasPublishedTariffs ? publishedTariffs.length : messageIds.length}
            </p>
            <p className="text-sm text-blue-600">Tariffs Published</p>
          </div>
        </div>

        {/* Warning when no tariffs were published */}
        {publishedTariffs.length === 0 && messageIds.length === 0 && result.isSuccess && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-800">No Tariffs Published</h4>
                <p className="text-sm text-blue-700 mt-1">
                  The publish operation completed successfully, but no tariffs were published.
                  This may indicate that:
                </p>
                <ul className="text-sm text-blue-700 mt-2 list-disc list-inside space-y-1">
                  <li>No tariffs are configured for OCPI publishing</li>
                  <li>The selected operator has no active pricing plans</li>
                  <li>Tariff configuration may be incomplete or missing required fields</li>
                </ul>
                <p className="text-sm text-blue-700 mt-2">
                  Please verify your tariff configuration in the Nayax Core portal.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Request ID */}
        {result.response.requestId && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">Request ID</p>
            <p className="text-sm font-mono text-gray-700">{result.response.requestId}</p>
          </div>
        )}

        {/* Published Tariffs Table (new enhanced view) */}
        {hasPublishedTariffs && (
          <div className="border rounded-lg">
            <Button
              variant="ghost"
              className="w-full justify-between p-4 h-auto"
              onClick={() => setShowPublishedTariffs(!showPublishedTariffs)}
            >
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-green-600" />
                <span className="font-medium">
                  Published Tariffs ({publishedTariffs.length})
                </span>
              </div>
              {showPublishedTariffs ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </Button>

            {showPublishedTariffs && (
              <div className="border-t">
                {/* Search input */}
                <div className="p-3 border-b bg-gray-50">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by actor, tariff type, or relation ID..."
                      value={actorSearchQuery}
                      onChange={(e) => setActorSearchQuery(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                  {actorSearchQuery && (
                    <p className="text-xs text-gray-500 mt-2">
                      Showing {filteredPublishedTariffs.length} of {publishedTariffs.length} tariffs
                    </p>
                  )}
                </div>
                <Table>
                  <TableHeader className={GRID_STYLES.header.background}>
                    <TableRow className={cn(GRID_STYLES.header.background, 'border-b', GRID_STYLES.border)}>
                      <TableHead className={GRID_STYLES.header.text}>Actor</TableHead>
                      <TableHead className={GRID_STYLES.header.text}>Tariff Type</TableHead>
                      <TableHead className={GRID_STYLES.header.text}>Relation ID</TableHead>
                      <TableHead className={GRID_STYLES.header.text}>Message ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPublishedTariffs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                          No tariffs found matching "{actorSearchQuery}"
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPublishedTariffs.map((tariff) => {
                        const actorDisplay = tariff.actorName ?? (tariff.actorId ? `Actor ${tariff.actorId}` : 'Operator-level');
                        return (
                          <TableRow key={tariff.messageId} className={GRID_STYLES.row.hover}>
                            <TableCell>
                              <HighlightedText text={actorDisplay} query={actorSearchQuery} />
                              {tariff.actorId && tariff.actorName && (
                                <span className="text-xs text-gray-500 ml-1">
                                  (ID: <HighlightedText text={tariff.actorId.toString()} query={actorSearchQuery} />)
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                                <HighlightedText text={tariff.tariffType} query={actorSearchQuery} />
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-mono">
                                <HighlightedText text={tariff.relationId.toString()} query={actorSearchQuery} />
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-xs font-mono text-gray-500">{tariff.messageId}</span>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {/* Legacy Published Tariff IDs (fallback when publishedTariffs is not available) */}
        {!hasPublishedTariffs && messageIds.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">
              Published Tariff IDs ({messageIds.length})
            </h4>
            <div className="flex flex-wrap gap-2">
              {messageIds.slice(0, 30).map((id, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-mono"
                >
                  {id}
                </span>
              ))}
              {messageIds.length > 30 && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                  +{messageIds.length - 30} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Error Message */}
        {!result.isSuccess && result.response.errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-2">Error</h4>
            <p className="text-sm text-red-700">{result.response.errorMessage}</p>
            {result.response.errorCode && (
              <p className="text-xs text-red-600 mt-1">
                Error Code: {result.response.errorCode}
              </p>
            )}
          </div>
        )}

        {/* Errors List */}
        {result.errors && result.errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-2">Errors</h4>
            <ul className="space-y-1">
              {result.errors.map((error, index) => (
                <li key={index} className="text-sm text-red-700 flex items-start gap-2">
                  <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}
        </CardContent>
      )}
    </Card>
  );
}
